import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Cookie, ShieldCheck, MapPin, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CookieConsentModal from './CookieConsentModal';
import { grantAnalyticsConsent, revokeAnalyticsConsent } from '@/lib/analytics';
import { isLexContext } from '@/utils/lexDomain';
import { useAuth } from '@/contexts/AuthContext';

export default function CookieBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // lex.ivoliq: Navy/Gold-Styling + Sie-Form (Styleguide /app/design_guidelines_lex.json)
  // — greift auf lex-Hosts/?lex=1 UND für eingeloggte lex-Anwälte auf der Hauptdomain.
  const lex = isLexContext(user);

  const getConsentAnonymousId = () => {
    let consentId = localStorage.getItem('ivoliq_cookie_consent_id');
    if (!consentId) {
      consentId = window.crypto?.randomUUID?.() || `consent_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('ivoliq_cookie_consent_id', consentId);
    }
    return consentId;
  };

  useEffect(() => {
    // Check if user has already made a choice
    const hasConsent = localStorage.getItem('ivoliq_cookie_consent');

    if (!hasConsent) {
      setShowBanner(true);
    } else {
      loadExistingPreferences();
    }
  }, []);

  const loadExistingPreferences = () => {
    const analytics = localStorage.getItem('analytics-consent') === 'true';
    const marketing = localStorage.getItem('marketing-consent') === 'true';

    if (analytics) {
      grantAnalyticsConsent();
      enableGoogleAnalytics();
      enableMicrosoftClarity();
    }

    if (marketing) {
      enableGoogleAds();
    }
  };

  const enableGoogleAnalytics = () => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const enableMicrosoftClarity = () => {
    // Dynamically load Clarity script only after consent (DSGVO compliance)
    if (window.clarity) return; // Already loaded
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "ujffk11rf1");
  };

  const enableGoogleAds = () => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'ads_data_redaction': false
      });
    }
  };

  const handleSavePreferences = async (preferences) => {
    // Save to localStorage
    localStorage.setItem('analytics-consent', preferences.analytics.toString());
    localStorage.setItem('marketing-consent', preferences.marketing.toString());
    localStorage.setItem('ivoliq_cookie_consent', 'true');

    // Initialize services based on preferences
    if (preferences.analytics) {
      grantAnalyticsConsent();
      enableGoogleAnalytics();
      enableMicrosoftClarity();
    } else {
      revokeAnalyticsConsent();
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }
      // Clarity: not loaded if no consent, nothing to disable
    }

    if (preferences.marketing) {
      enableGoogleAds();
    } else {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      }
    }

    // Log consent to backend (GDPR compliance)
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/auth/cookie-consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          consent_type: preferences.analytics && preferences.marketing ? 'all' : preferences.analytics ? 'analytics_only' : preferences.marketing ? 'marketing_only' : 'necessary_only',
          analytics: preferences.analytics,
          marketing: preferences.marketing,
          anonymous_id: getConsentAnonymousId(),
        }),
      });
    } catch (error) {
      // Consent logging failed silently - non-blocking
    }

    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    handleSavePreferences({ necessary: true, analytics: true, marketing: true });
  };

  const handleNecessaryOnly = () => {
    handleSavePreferences({ necessary: true, analytics: false, marketing: false });
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Floating Card statt Full-Width-Balken: weniger invasiv, positives Framing (Nudging),
          Ablehnen-Option bleibt auf erster Ebene gleichwertig klickbar (DSK-konform) */}
      <div
        className={`fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-40 border shadow-2xl ${lex ? "" : "rounded-2xl border-gray-200 bg-white"}`}
        data-testid="cookie-consent-banner"
        style={{ animation: 'slideUp 0.4s ease-out', ...(lex ? { background: '#0A1128', borderColor: 'rgba(197,160,89,0.35)' } : {}) }}
      >
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className={`p-2.5 flex-shrink-0 ${lex ? "" : "rounded-xl bg-emerald-50"}`} style={lex ? { background: 'rgba(197,160,89,0.12)' } : undefined}>
              <Cookie className="h-5 w-5" style={{ color: lex ? '#C5A059' : '#059669' }} />
            </div>
            <div className="min-w-0">
              <h3 className={`text-base font-bold mb-1 ${lex ? "lex-serif text-white" : "text-gray-900"}`}>
                {lex ? 'Helfen Sie uns, lex.ivoliq besser zu machen' : t('cookies.title', 'Hilf uns, ivoliq besser zu machen')}
              </h3>
              <p className={`text-sm leading-snug ${lex ? "text-slate-300" : "text-gray-600"}`}>
                {lex
                  ? 'Mit Ihrer Zustimmung sehen wir anonymisiert, welche Funktionen Sie weiterbringen — und machen lex.ivoliq gezielt besser für Sie.'
                  : t(
                      'cookies.message',
                      'Mit deiner Zustimmung sehen wir anonymisiert, welche Funktionen dich weiterbringen — und machen ivoliq gezielt besser für dich.'
                    )}
              </p>
            </div>
          </div>

          {/* Trust-Signale (Nudging über Vertrauen statt Druck) */}
          <div className={`mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] ${lex ? "text-slate-400" : "text-gray-500"}`}>
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" style={{ color: lex ? '#C5A059' : '#059669' }} /> Server in der EU</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" style={{ color: lex ? '#C5A059' : '#059669' }} /> DSGVO-konform</span>
            <span className="inline-flex items-center gap-1"><Undo2 className="h-3 w-3" style={{ color: lex ? '#C5A059' : '#059669' }} /> Jederzeit widerrufbar</span>
          </div>

          <div className="mt-4 space-y-2">
            <Button
              onClick={handleAcceptAll}
              className={`w-full font-semibold py-5 transition-all hover:-translate-y-0.5 ${
                lex
                  ? "rounded-none bg-[#C5A059] hover:bg-[#D4B26E] text-[#0A1128] uppercase tracking-widest text-xs"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white text-base"
              }`}
              data-testid="cookie-banner-accept-all-button"
            >
              {t('cookies.accept', 'Alle akzeptieren')}
            </Button>
            <Button
              onClick={handleNecessaryOnly}
              variant="ghost"
              className={`w-full font-normal ${
                lex
                  ? "rounded-none text-slate-400 hover:text-white hover:bg-white/10"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              data-testid="cookie-banner-necessary-only-button"
            >
              {t('cookies.decline', 'Nur notwendige')}
            </Button>
          </div>

          <div className="mt-2 flex items-center justify-center gap-3 text-xs">
            <button
              onClick={() => setShowModal(true)}
              className={lex ? "text-slate-500 underline hover:text-slate-300" : "text-gray-400 underline hover:text-gray-600"}
              data-testid="cookie-banner-customize-button"
            >
              {t('cookies.customize', 'Anpassen')}
            </button>
            <span className={lex ? "text-slate-600" : "text-gray-300"}>·</span>
            <Link to="/privacy" className={lex ? "text-slate-500 underline hover:text-slate-300" : "text-gray-400 underline hover:text-gray-600"} data-testid="cookie-banner-privacy-link">
              {t('cookies.learnMore', 'Datenschutz')}
            </Link>
          </div>
        </div>
      </div>

      {/* Modal */}
      <CookieConsentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSavePreferences}
      />

      {/* Slide-up animation */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
